import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { updateV2Field } from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import type {
  LanguageFluency,
  LanguageItemV2,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationTypesV2";
import { validateRequiredFields } from "@/utils/formValidationV2";
import { usePricingFormula } from "@/hooks/usePricingFormula";

const FLUENCY_OPTIONS: { value: LanguageFluency; label: string }[] = [
  { value: "native", label: "Native" },
  { value: "fluent", label: "Fluent" },
  { value: "conversational", label: "Conversational" },
];

const PRESET_LANGUAGES_NATIONAL = [
  "English",
  "Hindi",
  "Bengali",
  "Marathi",
  "Tamil",
  "Telugu",
  "Gujarati",
  "Kannada",
  "Malayalam",
  "Punjabi",
  "Bhojpuri",
];

const PRESET_LANGUAGES_INTERNATIONAL = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Russian",
  "Arabic",
  "Indonesian",
  "Korean",
  "Hindi",
  "Mandarin Chinese",
  "Portuguese",
  "Turkish",
  "Thai",
];

const normalize = (v: string) => v.trim().replace(/\s+/g, " ");
const keyify = (v: string) => normalize(v).toLowerCase();

const isEnglish = (name: string) => keyify(name) === "english";

const isFluencyRequiredForLanguage = (
  language: LanguageItemV2,
  isIndianCreator: boolean,
) => {
  return isIndianCreator && isEnglish(language.name);
};

const hasMissingRequiredFluency = (
  languages: LanguageItemV2[],
  isIndianCreator: boolean,
) =>
  languages.some(
    (language) =>
      isFluencyRequiredForLanguage(language, isIndianCreator) &&
      !language.fluency,
  );

// ── exported for ApplicationStep4 Proceed button ──
// Validates: at least one language selected AND required fluency rules
export function validateLanguages(
  languages: LanguageItemV2[],
  isIndianCreator = true,
) {
  const hasMissingFluency = hasMissingRequiredFluency(
    languages,
    isIndianCreator,
  );

  const requiredFields: Array<"languages" | "languageFluency"> = ["languages"];

  if (hasMissingFluency) {
    requiredFields.push("languageFluency");
  }

  return validateRequiredFields(
    {
      languages: languages.length > 0 ? "ok" : "",
      languageFluency: hasMissingFluency ? "" : "ok",
    },
    requiredFields,
  );
}

const LanguagesSection = ({
  forceValidate = false,
}: {
  forceValidate?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const languages = useAppSelector((s) => s.signUpV2.v2Extras.languages) || [];
  const { ucdT } = usePricingFormula();

  const isIndianCreator = ucdT?.country === "IN";

  const [isOtherOpen, setIsOtherOpen] = useState(false);
  const [otherInput, setOtherInput] = useState("");

  const [touched, setTouched] = useState(false);
  const shouldValidate = touched || forceValidate;

  const noLanguageError =
    shouldValidate && languages.length === 0
      ? "Please select at least one language"
      : undefined;

  const fluencyError =
    shouldValidate && hasMissingRequiredFluency(languages, isIndianCreator)
      ? "Please select fluency for English"
      : undefined;

  const selectedKeySet = useMemo(() => {
    return new Set(languages.map((l) => keyify(l.name)));
  }, [languages]);

  const addLanguage = (nameRaw: string, isCustom = false) => {
    const name = normalize(nameRaw);
    if (!name) return;

    const k = keyify(name);
    if (selectedKeySet.has(k)) {
      setOtherInput("");
      return;
    }

    setTouched(true);
    const next: LanguageItemV2[] = [
      ...languages,
      {
        id: crypto.randomUUID(),
        name,
        fluency: "",
        isCustom,
        createdAt: new Date().toISOString(),
      },
    ];
    dispatch(updateV2Field("languages", next));
    setOtherInput("");
  };

  const removeLanguage = (id: string) => {
    setTouched(true);
    const next = languages.filter((l) => l.id !== id);
    dispatch(updateV2Field("languages", next));
  };

  const setFluency = (id: string, fluency: LanguageItemV2["fluency"]) => {
    setTouched(true);
    const next = languages.map((l) => (l.id === id ? { ...l, fluency } : l));
    dispatch(updateV2Field("languages", next));
  };

  const togglePreset = (name: string) => {
    const exists = languages.find((l) => keyify(l.name) === keyify(name));
    if (exists) {
      removeLanguage(exists.id);
      return;
    }
    addLanguage(name, false);
  };

  const canAddOther = normalize(otherInput).length > 0;

  const getFluencyLabel = (language: LanguageItemV2) =>
    isFluencyRequiredForLanguage(language, isIndianCreator)
      ? "Fluency *"
      : "Fluency";

  const PRESET_LANGUAGES = isIndianCreator
    ? PRESET_LANGUAGES_NATIONAL
    : PRESET_LANGUAGES_INTERNATIONAL;

  return (
    <div id="languages" className="w-full max-w-md">
      <div className="mb-2">
        <label className="text-sm tracking-normal block">
          <span className="text-[#121217] font-medium">
            Languages you can create in{" "}
          </span>
          <span className="text-[#475467]">(Select all that apply)</span>
        </label>
        <p className="text-[10px] text-[#475467] mt-1">
          Pick the languages you can confidently write/speak on camera. Set
          fluency for better matching.
        </p>
      </div>

      {languages.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {languages.map((l) => (
            <div
              key={l.id}
              className="flex items-center gap-2 border border-[#FF6600] bg-[#FFF4EC] rounded-[12px] px-2 py-1.5"
            >
              <span className="text-[12px] font-medium text-[#121217]">
                {l.name}
              </span>

              <FluencyDropdown
                value={l.fluency}
                onChange={(val) =>
                  setFluency(l.id, val as LanguageItemV2["fluency"])
                }
                placeholder={getFluencyLabel(l)}
                hasError={
                  !!fluencyError &&
                  isFluencyRequiredForLanguage(l, isIndianCreator) &&
                  !l.fluency
                }
              />

              <button
                type="button"
                onClick={() => removeLanguage(l.id)}
                className="p-1 rounded-lg hover:bg-black/5"
                aria-label={`Remove ${l.name}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {PRESET_LANGUAGES.filter(
          (name) => !selectedKeySet.has(keyify(name)),
        ).map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => togglePreset(name)}
            className={`border px-3 py-2 rounded-xl text-[12px] leading-none select-none transition-colors
              ${
                noLanguageError
                  ? "border-red-300 bg-white"
                  : "border-[#D8D8DD] bg-white"
              }`}
          >
            {name}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setIsOtherOpen((p) => !p)}
          className={`border px-3 py-2 rounded-xl text-[12px] leading-none select-none flex items-center gap-1
            ${isOtherOpen ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD] bg-white"}`}
        >
          <span className="text-[#FF6600] font-bold">+</span>
          Other
        </button>
      </div>

      {noLanguageError && (
        <p className="mt-1.5 text-[10px] text-red-500">{noLanguageError}</p>
      )}
      {fluencyError && (
        <p className="mt-1.5 text-[10px] text-red-500">{fluencyError}</p>
      )}

      {isOtherOpen && (
        <div className="mt-3">
          <div className="flex items-center gap-2">
            <input
              value={otherInput}
              onChange={(e) => setOtherInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (canAddOther) addLanguage(otherInput, true);
                }
              }}
              placeholder="Type a language (e.g. Urdu)"
              className="flex-1 border border-[#D8D8DD] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FF6600]"
            />
            <button
              type="button"
              onClick={() => addLanguage(otherInput, true)}
              disabled={!canAddOther}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition
                ${
                  canAddOther
                    ? "bg-[#FF6600] text-white hover:opacity-90"
                    : "bg-[#F2F4F7] text-[#98A2B3] cursor-not-allowed"
                }`}
            >
              Add
            </button>
          </div>
          <p className="text-[10px] text-[#667085] mt-1">
            Tip: press <span className="font-medium">Enter</span> to add
            quickly.
          </p>
        </div>
      )}
    </div>
  );
};

export default LanguagesSection;

// ─────────────────────────────────────────────
// FluencyDropdown
// ─────────────────────────────────────────────
const FluencyDropdown = ({
  value,
  onChange,
  placeholder,
  hasError,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  hasError?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = FLUENCY_OPTIONS.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`
          flex items-center gap-1.5 bg-white border rounded-full
          text-[11px] px-2.5 py-1 outline-none transition-all duration-200
          ${
            open
              ? "border-[#F97316] shadow-[0_0_0_2px_rgba(249,115,22,0.12)]"
              : hasError
                ? "border-red-400 shadow-[0_0_0_2px_rgba(239,68,68,0.1)]"
                : "border-[#FDE2D1] hover:border-[#F97316]"
          }
        `}
      >
        <span
          className={
            selected
              ? "text-[#121217]"
              : hasError
                ? "text-red-400"
                : "text-[#9CA3AF]"
          }
        >
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={12}
          strokeWidth={2.5}
          className={`transition-transform duration-200 shrink-0 ${
            open ? "rotate-180" : ""
          } ${hasError && !open ? "text-red-400" : "text-[#F97316]"}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1.5 left-0 min-w-[120px] bg-white rounded-xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(0,0,0,0.08)] overflow-hidden py-1">
          {FLUENCY_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`
                w-full flex items-center justify-between px-3 py-1.5
                text-[12px] text-left transition-colors duration-150
                ${
                  value === o.value
                    ? "bg-[#FFF7F0] text-[#F97316]"
                    : "text-[#374151] hover:bg-[#F9FAFB]"
                }
              `}
            >
              <span>{o.label}</span>
              {value === o.value && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
